import { Injectable } from '@angular/core';
import {
  Firestore,
  doc,
  onSnapshot,
  collection,
  Unsubscribe,
  getDocs,
  updateDoc
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { UserDocument } from '../interfaces/user-document.interface';
import { AuthService } from './auth.service';
import { User } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private userDataSubject = new BehaviorSubject<UserDocument | null>(null);
  public userData$ = this.userDataSubject.asObservable();

  private destroy$ = new Subject<void>();
  private currentUserDataUnsubscribe: Unsubscribe | null = null;

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
  ) {
    this.initCentralizedUserDataListener();
  }

  private initCentralizedUserDataListener(): void {
    this.authService.userSubject.pipe(
      takeUntil(this.destroy$),
      switchMap((user: User | null) => {
        if (this.currentUserDataUnsubscribe) {
          this.currentUserDataUnsubscribe();
          this.currentUserDataUnsubscribe = null;
        }

        if (user) {
          const userDocRef = doc(this.firestore, `User/${user.uid}`);
          return new Observable<UserDocument | null>(subscriber => {
            const unsubscribe = onSnapshot(
              userDocRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  const userData = { id: snapshot.id, ...snapshot.data() } as UserDocument;
                  subscriber.next(userData);
                } else {
                  console.log('User document not found');
                  subscriber.next(null);
                }
              },
              (error) => {
                console.error('Error fetching user data in realtime:', error);
                subscriber.error(error);
              }
            );
            this.currentUserDataUnsubscribe = unsubscribe;
            return () => unsubscribe();
          });
        } else {
          return of(null);
        }
      })
    ).subscribe({
      next: (userData) => {
        this.userDataSubject.next(userData);
      },
      error: (error) => {
        console.error('DataService centralized user data error:', error);
        this.userDataSubject.next(null);
      }
    });
  }

  getCurrentUserData(): UserDocument | null {
    return this.userDataSubject.value;
  }

  updateUserData(updatedFields: Partial<UserDocument>, uid: string): Promise<void> {
    const userDocRef = doc(this.firestore, `User/${uid}`);
    return updateDoc(userDocRef, updatedFields);
  }

  hasRole(role: string): boolean {
    const userData = this.getCurrentUserData();
    return userData?.role?.includes(role) || false;
  }

  isPharmacy(): boolean {
    return this.hasRole('pharmacy');
  }

  isControlCenter(): boolean {
    return this.hasRole('controlCenter');
  }

  isCsyt(): boolean {
    return this.hasRole('csyt');
  }

  isPaymentCenter(): boolean {
    return this.hasRole('paymentcenter');
  }


  isMember(): boolean {
    const userData = this.getCurrentUserData();
    if (!userData) {
      return false; // Guard against null user data
    }
    return (userData.diamonds || 0) >= 5000;
  }

  async fetchAllProblems(): Promise<any[]> {
    const cacheKey = 'all_problems';
    const cacheTimeKey = 'all_problems_time';
    const cacheExpiryMs = 7 * 24 * 60 * 60 * 1000; // 7 ngày
    const now = Date.now();

    const cachedData = localStorage.getItem(cacheKey);
    const cachedTime = localStorage.getItem(cacheTimeKey);

    if (cachedData && cachedTime && now - +cachedTime < cacheExpiryMs) {
      const problems = JSON.parse(cachedData);
      console.log('Dữ liệu all_problems từ cache:', problems);
      return Promise.resolve(problems);
    }

    const problemsRef = collection(this.firestore, 'problems');
    try {
      const querySnapshot = await getDocs(problemsRef);
      const problems_1 = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Tải mới all_problems từ Firestore:', problems_1);

      localStorage.setItem(cacheKey, JSON.stringify(problems_1));
      localStorage.setItem(cacheTimeKey, now.toString());
      return problems_1;
    } catch (error) {
      console.error('Lỗi khi tải all_problems:', error);
      return [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.currentUserDataUnsubscribe) {
      this.currentUserDataUnsubscribe();
    }
  }
}
